import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { formatBrandMoney, resolveLiveVatRate, getBrandLocale } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ClipboardList, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, Clock, List, LayoutList, Package, Truck, DollarSign, FileText, UserCheck } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { makePageT } from '../../shared/utils/pageI18n';
import VentesListPageChrome, { facturationTableScrollClass } from './components/VentesListPageChrome';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  mapVentesStatCards,
  useCrudModalBodyLock,
  initVentesColumnVisibility,
  buildVentesQawaemColumnDefs,
  isVentesColumnVisible,
  buildVentesBulkDeleteFilterAction,
  computeVentesRowSelection,
  resolveVentesEntityReference,
  useVentesEntityReference,
  VENTES_REF_INPUT_CLASS,
  VentesConfigNotice,
  sliceVentesListItems,
  matchesVentesListDateRange,
} from './ventesListPageUtils';
import { useVentesConfig } from './useVentesConfig';
import { useInventoryConfig } from '../inventaire/useInventoryConfig';
import { createStockReservationsForSalesOrder } from './ventesCrossModuleUtils';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { fetchCatalogProducts } from '../articles/catalogProductOptions';
import { computeDefaultExpectedDeliveryDate } from './ventesConfigDefaults';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('fr', fr);
registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

const CommandesClientsPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.ventes.pages.commandesClientsPage');
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(dateLocale) : '-';
  const ventesConfig = useVentesConfig();
  const { settings } = useSettings();
  const vatRate = useMemo(
    () => resolveLiveVatRate(settings, ventesConfig),
    [settings?.taxes?.tva, ventesConfig.defaultTaxRate, settings],
  );

  const getPaymentMethodLabel = (method) => {
    const map = {
      cash: p('method_cash'),
      check: p('method_check'),
      transfer: p('method_transfer'),
      mada: p('method_mada'),
      card: p('method_card'),
      credit: p('method_credit'),
    };
    return map[method] || method || p('na');
  };

  const paymentMethodOptions = useMemo(
    () => [
      { value: 'cash', label: p('method_cash') },
      { value: 'check', label: p('method_check') },
      { value: 'transfer', label: p('method_transfer') },
      { value: 'mada', label: p('method_mada') },
      { value: 'card', label: p('method_card') },
      { value: 'credit', label: p('method_credit') },
    ],
    [t],
  );

  const getStatusLabel = (status) => {
    const map = {
      pending: p('status_pending'),
      confirmed: p('status_confirmed'),
      preparing: p('status_preparing'),
      shipped: p('status_shipped'),
      delivered: p('status_delivered'),
      cancelled: p('status_cancelled'),
    };
    return map[status] || status || p('em_dash');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnStatusButton = event.target.closest('[data-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const inventoryConfig = useInventoryConfig();
  useArticlesConfig();
  const { dateRange } = useSaudiListDateRange();

  useEffect(() => {
    setViewMode(ventesConfig.defaultView || 'table');
  }, [ventesConfig.defaultView]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  useCrudModalBodyLock(showModal, showViewModal);
  const generateOrderRef = useVentesEntityReference('salesOrder', orders);
  
  const [formTab, setFormTab] = useState('order');
  
  const [formData, setFormData] = useState({ 
    order_number: '',
    customer_id: '',
    customer_name: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    status: 'pending',
    subtotal: '',
    tax_amount: '',
    total_amount: '',
    shipping_cost: '',
    payment_method: '',
    notes: ''
  });
  
  // Products state
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_orders');
  const [quickFilter, setQuickFilter] = useState('all');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  
  const orderFormTabs = useMemo(() => ([
    { id: 'order', label: p('section_order_info'), icon: ClipboardList, iconColor: 'text-emerald-400' },
    { id: 'products', label: p('section_products_amounts'), icon: Package, iconColor: 'text-green-400' },
  ]), [p]);

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const thFilterClass = 'text-white font-semibold py-4';

  const tableColumnDefs = useMemo(
    () => [
      { key: 'order_number', label: p('col_order_number'), required: true },
      { key: 'customer_name', label: p('col_customer') },
      { key: 'order_date', label: p('col_order_date') },
      { key: 'expected_delivery_date', label: p('col_delivery_date') },
      { key: 'total_amount', label: p('col_amount') },
      { key: 'payment_method', label: p('col_payment') },
      { key: 'status', label: p('col_status') },
    ],
    [p],
  );
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initVentesColumnVisibility(tableColumnDefs.map((c) => c.key)),
  );
  const qawaemColumnDefs = useMemo(
    () => buildVentesQawaemColumnDefs(tableColumnDefs, columnVisibility),
    [tableColumnDefs, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const showCol = (key) => isVentesColumnVisible(columnVisibility, key);
  const tableColSpan = tableColumnDefs.filter((c) => showCol(c.key)).length + 2;

  useEffect(() => { 
    loadOrders();
    loadProducts();
    loadClients();
  }, []);
  
  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      setAvailableProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };
  
  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const response = await localApiAdapter.get('/facturation/clients');
      setAvailableClients(response?.data || response || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setAvailableClients([]);
    } finally {
      setLoadingClients(false);
    }
  };
  
  const handleClientSelection = (clientId) => {
    if (!clientId) {
      setFormData({
        ...formData,
        customer_id: '',
        customer_name: ''
      });
      return;
    }
    
    const client = availableClients.find(c => c.id.toString() === clientId.toString());
    if (client) {
      setFormData({
        ...formData,
        customer_id: clientId,
        customer_name: client.company_name || client.name || ''
      });
    }
  };
  
  const addOrderItem = () => {
    setOrderItems([...orderItems, {
      id: Date.now(),
      product_id: '',
      product_name: '',
      quantity: 1,
      unit_price: 0,
      line_total: 0
    }]);
  };
  
  const removeOrderItem = (id) => {
    setOrderItems(orderItems.filter(item => item.id !== id));
  };
  
  const updateOrderItem = (id, field, value) => {
    setOrderItems(orderItems.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        
        // If product selected, auto-fill price
        if (field === 'product_id') {
          const product = availableProducts.find(p => p.id.toString() === value.toString());
          if (product) {
            updated.product_name = product.name;
            updated.unit_price = parseFloat(product.price || 0);
          }
        }
        
        // Calculate line total
        updated.line_total = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.unit_price) || 0);
        
        return updated;
      }
      return item;
    }));
  };
  
  // Auto-calculate totals when items change
  useEffect(() => {
    const subtotal = orderItems.reduce((sum, item) => sum + (parseFloat(item.line_total) || 0), 0);
    const taxAmount = subtotal * (vatRate / 100);
    const total = subtotal + taxAmount + (parseFloat(formData.shipping_cost) || 0);
    
    setFormData(prev => ({
      ...prev,
      subtotal: subtotal.toFixed(2),
      tax_amount: taxAmount.toFixed(2),
      total_amount: total.toFixed(2)
    }));
  }, [orderItems, formData.shipping_cost, vatRate]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadOrders = async () => {
    try {
      const response = await localApiAdapter.get('/ventes/sales-orders');
      const rows = response?.data || response || [];
      setOrders(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading sales orders:', error);
      setOrders([]);
    }
  };

  const stats = useMemo(() => ({
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    preparing: orders.filter(o => o.status === 'preparing').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
  }), [orders]);

  const mainTabs = useMemo(() => [
    { id: 'all_orders', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: AlertCircle },
    { id: 'par_client', name: p('tab_by_customer'), icon: UserCheck },
  ], [t]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'pending', name: p('filter_pending'), icon: Clock },
    { id: 'confirmed', name: p('filter_confirmed'), icon: CheckCircle },
    { id: 'shipped', name: p('filter_shipped'), icon: Truck },
  ], [t]);

  const orderDistribution = useMemo(() => ({
    all_orders: {
      title: p('overview_title'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { filterKey: 'confirmed', name: p('dist_confirmed'), count: stats.confirmed, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'preparing', name: p('dist_preparing'), count: stats.preparing, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'delivered', name: p('dist_delivered'), count: stats.delivered, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'in_progress', name: p('dist_in_progress'), count: stats.pending + stats.confirmed + stats.preparing + stats.shipped, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.delivered, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_client: {
      title: p('dist_by_customer_title'),
      data: (() => {
        const customerCounts = {};
        orders.forEach(o => {
          const customer = o.customer_name || '__undefined__';
          customerCounts[customer] = (customerCounts[customer] || 0) + 1;
        });
        return Object.entries(customerCounts)
          .slice(0, 3)
          .map(([name, count], idx) => ({
            filterKey: name,
            name: name === '__undefined__' ? p('customer_undefined') : name,
            count,
            color: idx === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' : idx === 1 ? 'bg-gradient-to-r from-purple-500 to-purple-600' : 'bg-gradient-to-r from-pink-500 to-pink-600',
          }));
      })(),
    },
  }), [orders, stats, t]);

  const distributionInfo = orderDistribution[mainViewMode] || orderDistribution.all_orders;

  const orderStatusFilterOptions = useMemo(() => ([
    { value: 'pending', label: p('status_pending') },
    { value: 'confirmed', label: p('status_confirmed') },
    { value: 'preparing', label: p('status_preparing') },
    { value: 'shipped', label: p('status_shipped') },
    { value: 'delivered', label: p('status_delivered') },
    { value: 'cancelled', label: p('status_cancelled') },
  ]), [p]);

  const columnFilterRules = useMemo(() => ([
    textColumnRule('order_number'),
    textColumnRule('customer_name'),
    textColumnRule('order_date', (r) => (r.order_date ? fmtDate(r.order_date) : '')),
    textColumnRule('expected_delivery_date', (r) => (r.expected_delivery_date ? fmtDate(r.expected_delivery_date) : '')),
    textColumnRule('total_amount', (r) => String(r.total_amount ?? '')),
    textColumnRule('payment_method', (r) => getPaymentMethodLabel(r.payment_method)),
    exactColumnRule('status', (r) => r.status ?? ''),
  ]), [p]);

  const baseFilteredOrders = useMemo(() => orders.filter(o => {
    if (!matchesVentesListDateRange(o.order_date, dateRange)) return false;
    if (quickFilter !== 'all' && o.status !== quickFilter) return false;
    if (searchTerm && !o.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) && !o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_orders':
          if (activeDistributionFilter === 'pending' && o.status !== 'pending') return false;
          if (activeDistributionFilter === 'confirmed' && o.status !== 'confirmed') return false;
          if (activeDistributionFilter === 'preparing' && o.status !== 'preparing') return false;
          if (activeDistributionFilter === 'delivered' && o.status !== 'delivered') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'in_progress' && !['pending', 'confirmed', 'preparing', 'shipped'].includes(o.status)) return false;
          if (activeDistributionFilter === 'completed' && o.status !== 'delivered') return false;
          break;
        case 'par_client':
          if (activeDistributionFilter === '__undefined__' && o.customer_name) return false;
          if (activeDistributionFilter !== '__undefined__' && o.customer_name !== activeDistributionFilter) return false;
          break;
      }
    }
    
    return true;
  }), [orders, quickFilter, searchTerm, activeDistributionFilter, mainViewMode, dateRange]);

  const filteredOrders = useMemo(
    () => applyColumnFilters(baseFilteredOrders, filters, columnFilterRules),
    [baseFilteredOrders, filters, columnFilterRules],
  );

  const filteredOrderIds = useMemo(() => filteredOrders.map((o) => o.id), [filteredOrders]);
  const displayedOrders = useMemo(
    () => sliceVentesListItems(filteredOrders, ventesConfig.itemsPerPage),
    [filteredOrders, ventesConfig.itemsPerPage],
  );
  const { allRowsSelected, someRowsSelected } = useMemo(
    () => computeVentesRowSelection(filteredOrderIds, selectedRecords),
    [filteredOrderIds, selectedRecords],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (orderItems.length === 0) {
      alert(p('alert_add_product'));
      return;
    }

    if (ventesConfig.requireCustomer && !formData.customer_id) {
      alert(p('placeholder_select_customer'));
      return;
    }
    
    try {
      const orderNumber = resolveVentesEntityReference(
        settings,
        'salesOrder',
        orders,
        formData.order_number,
      );
      const submitData = { 
        order_number: orderNumber,
        customer_id: parseInt(formData.customer_id) || null,
        customer_name: formData.customer_name,
        order_date: formData.order_date || new Date().toISOString().split('T')[0],
        expected_delivery_date: formData.expected_delivery_date,
        status: formData.status,
        subtotal: parseFloat(formData.subtotal) || 0,
        tax_amount: parseFloat(formData.tax_amount) || 0,
        total_amount: parseFloat(formData.total_amount) || 0,
        shipping_cost: parseFloat(formData.shipping_cost) || 0,
        payment_method: formData.payment_method,
        notes: formData.notes,
        items: orderItems.map(item => ({
          product_id: parseInt(item.product_id),
          product_name: item.product_name,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          line_total: parseFloat(item.line_total)
        }))
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/ventes/sales-orders/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'أمر البيع' }));
      } else {
        await localApiAdapter.post('/ventes/sales-orders', submitData);
        await createStockReservationsForSalesOrder(
          localApiAdapter,
          inventoryConfig,
          { ...submitData, order_number: orderNumber },
        );
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'أمر البيع' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error') + ': ' + (error.message || p('alert_unknown_error')));
      return;
    } finally {
      resetForm();
      loadOrders();
    }
  };

  const handleEdit = (record) => {
    setFormTab('order');
    setEditingRecord(record);
    setFormData({ 
      order_number: record.order_number || '',
      customer_id: record.customer_id?.toString() || '',
      customer_name: record.customer_name || '',
      order_date: record.order_date || '',
      expected_delivery_date: record.expected_delivery_date || '',
      status: record.status || 'pending',
      subtotal: record.subtotal?.toString() || '',
      tax_amount: record.tax_amount?.toString() || '',
      total_amount: record.total_amount?.toString() || '',
      shipping_cost: record.shipping_cost?.toString() || '',
      payment_method: record.payment_method || '',
      notes: record.notes || ''
    });
    setOrderItems(record.items || []);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/ventes/sales-orders/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'أمر البيع' }));
        loadOrders();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_delete_error', { defaultValue: 'تعذّر حذف أمر البيع' }));
      }
    }
  };
  
  const handleConvertToLedger = async (order) => {
    if (!window.confirm(p('convert_confirm', { number: order.order_number }))) return;
    
    try {
      const response = await localApiAdapter.post(`/ventes/livre-des-ventes/convert/${order.id}`);
      alert(p('convert_success', { number: response.sale_number }));
      await loadOrders(); // Refresh to update UI
    } catch (error) {
      alert(p('convert_error') + ' ' + (error.response?.data?.error || error.message || p('alert_unknown_error')));
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/ventes/sales-orders/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'أمر بيع' }));
        setSelectedRecords([]);
        loadOrders();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_bulk_delete_error', { defaultValue: 'تعذّر حذف أوامر البيع المحددة' }));
      }
    }
  };

  const openCreateOrderModal = () => {
    if (!ventesConfig.enableOrders) return;
    const orderDate = new Date().toISOString().split('T')[0];
    setEditingRecord(null);
    setFormTab('order');
    setFormData({
      order_number: ventesConfig.autoGenerateOrderReference !== false ? generateOrderRef() : '',
      customer_id: '',
      customer_name: '',
      order_date: orderDate,
      expected_delivery_date: computeDefaultExpectedDeliveryDate(
        orderDate,
        ventesConfig.defaultDeliveryLeadTimeDays,
      ),
      status: 'pending',
      subtotal: '',
      tax_amount: '',
      total_amount: '',
      shipping_cost: '',
      payment_method: '',
      notes: ventesConfig.defaultPaymentTerms
        ? `${p('payment_conditions', { defaultValue: 'الشروط:' })} ${ventesConfig.defaultPaymentTerms}`
        : '',
    });
    setOrderItems([]);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ 
      order_number: '',
      customer_id: '',
      customer_name: '',
      order_date: new Date().toISOString().split('T')[0],
      expected_delivery_date: '',
      status: 'pending',
      subtotal: '',
      tax_amount: '',
      total_amount: '',
      shipping_cost: '',
      payment_method: '',
      notes: ''
    });
    setOrderItems([]);
    setEditingRecord(null);
    setFormTab('order');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'order_number', label: p('col_order_number') },
      { key: 'customer_name', label: p('col_customer') },
      { key: 'order_date', label: p('export_col_order_date') },
      { key: 'expected_delivery_date', label: p('export_col_delivery_date') },
      { key: 'total_amount', label: p('export_col_total_amount') },
      { key: 'status', label: p('col_status') },
      { key: 'payment_method', label: p('col_payment') },
      { key: 'notes', label: p('label_notes') },
    ];

    const fileName = `commandes_clients_${new Date().toISOString().split('T')[0]}`;
    try {
      exportToCSV(filteredOrders, headers, `${fileName}.csv`);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const filteredData = filteredOrders;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `<div class="report-content"><table><thead><tr><th>${p('col_order_number')}</th><th>${p('col_customer')}</th><th>${p('col_order_date')}</th><th>${p('col_amount')}</th><th>${p('col_status')}</th></tr></thead><tbody>${filteredData.map(item => `<tr><td>${item.order_number || p('na')}</td><td>${item.customer_name || p('na')}</td><td>${fmtDate(item.order_date)}</td><td>${formatBrandMoney(item.total_amount || 0)}</td><td>${getStatusLabel(item.status)}</td></tr>`).join('')}</tbody></table></div>`;
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_pdf_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      await generateReportExcel('commandes_clients', p('report_pdf_title'), {});
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [{ key: 'order_number', label: p('col_order_number') }, { key: 'customer_name', label: p('col_customer') }, { key: 'order_date', label: p('export_col_order_date') }, { key: 'total_amount', label: p('export_col_total_amount') }];
    const schema = { requiredFields: ['customer_name', 'order_date'], validators: { total_amount: (value) => !value || !isNaN(parseFloat(value)) } };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try { await localApiAdapter.post('/ventes/sales-orders', record); successCount++; } catch (err) {}
          }
          await loadOrders();
          alert(p('import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
      }
    }, '.csv,.xlsx,.xls');
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredOrderIds);
    }
  };
  const handleSelectRecord = (id) => { setSelectedRecords(selectedRecords.includes(id) ? selectedRecords.filter(rId => rId !== id) : [...selectedRecords, id]); };

  const statusOptions = [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'confirmed', label: p('status_confirmed'), icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'preparing', label: p('status_preparing'), icon: Package, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { value: 'shipped', label: p('status_shipped'), icon: Truck, style: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
    { value: 'delivered', label: p('status_delivered'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ];

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/ventes/sales-orders/${recordId}`, { status: newStatus });
      await loadOrders();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const listStats = useMemo(
    () => mapVentesStatCards([
      { key: 'total', label: p('stat_total_orders'), value: stats.total, icon: ClipboardList, variant: 'copper' },
      { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, variant: 'forest' },
      { key: 'confirmed', label: p('stat_confirmed'), value: stats.confirmed, icon: CheckCircle, variant: 'copper' },
      { key: 'shipped', label: p('stat_shipped'), value: stats.shipped, icon: Truck, variant: 'forest' },
    ]),
    [stats, p],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (<button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"><XCircle size={14} />{p('reset_filter')}</button>)}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
            <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)} title={p('filter_by_title', { name: item.name })} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }} />
              </div>
              <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })() })}</p>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
      <VentesConfigNotice
        message={!ventesConfig.enableOrders ? p('notice_orders_disabled', { defaultValue: 'أوامر العملاء معطّلة — فعّلها من إعدادات المبيعات ← أوامر العملاء.' }) : null}
      />
      <VentesListPageChrome
        t={t}
        p={p}
        qawaem={qawaem}
        isDashboardLightMode={isDashboardLightMode}
        title={p('title')}
        subtitle={p('subtitle')}
        icon={ClipboardList}
        listStats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildVentesBulkDeleteFilterAction({
          selectedCount: selectedRecords.length,
          onDelete: handleBulkDelete,
          label: p('bulk_delete_confirm', { count: selectedRecords.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search') })}
        onCreate={ventesConfig.enableOrders ? openCreateOrderModal : undefined}
        createLabel={p('new_order')}
        importExportState={{
          showImportDropdown,
          setShowImportDropdown,
          showExportDropdown,
          setShowExportDropdown,
          importDropdownRef,
          exportDropdownRef,
        }}
        importExportHandlers={{
          onImport: handleImport,
          onImportCSV: handleImport,
          onImportExcel: handleImport,
          onExportCSV: handleExport,
          onExportExcel: handleExportExcel,
          onExportPDF: handleExportPDF,
        }}
        isGeneratingPDF={isGeneratingPDF}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                        title={allRowsSelected ? p('deselect_all', { defaultValue: t('common.deselect_all') }) : p('select_all', { defaultValue: t('common.select_all') })}
                      />
                    </div>
                  </th>
                  <TableColumnHeaderFilter columnKey="order_number" label={p('col_order_number')} value={filters.order_number || ''} onChange={(v) => setFilter('order_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  {showCol('customer_name') ? (
                    <TableColumnHeaderFilter columnKey="customer_name" label={p('col_customer')} value={filters.customer_name || ''} onChange={(v) => setFilter('customer_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('order_date') ? (
                    <TableColumnHeaderFilter columnKey="order_date" label={p('col_order_date')} value={filters.order_date || ''} onChange={(v) => setFilter('order_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('expected_delivery_date') ? (
                    <TableColumnHeaderFilter columnKey="expected_delivery_date" label={p('col_delivery_date')} value={filters.expected_delivery_date || ''} onChange={(v) => setFilter('expected_delivery_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('total_amount') ? (
                    <TableColumnHeaderFilter columnKey="total_amount" label={p('col_amount')} value={filters.total_amount || ''} onChange={(v) => setFilter('total_amount', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('payment_method') ? (
                    <TableColumnHeaderFilter columnKey="payment_method" label={p('col_payment')} value={filters.payment_method || ''} onChange={(v) => setFilter('payment_method', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('status') ? (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={orderStatusFilterOptions} value={filters.status || ''} onChange={(v) => setFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedOrders.length > 0 ? (
                  displayedOrders.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{record.order_number}</span><span className="text-white/50 text-xs mt-0.5">{p('customer_prefix')} {record.customer_name}</span></div></td>
                      {showCol('customer_name') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{record.customer_name}</span><span className="text-white/50 text-xs mt-0.5">{p('label_id')}: {record.customer_id || p('na')}</span></div></td>
                      ) : null}
                      {showCol('order_date') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><div className="flex items-center gap-1.5 text-white font-semibold"><Clock size={14} className="text-white/50" />{fmtDate(record.order_date)}</div><span className="text-white/50 text-xs mt-0.5">{record.order_date ? p('days_ago', { count: Math.floor((Date.now() - new Date(record.order_date).getTime()) / (1000 * 60 * 60 * 24)) }) : ''}</span></div></td>
                      ) : null}
                      {showCol('expected_delivery_date') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{fmtDate(record.expected_delivery_date)}</span><span className="text-white/50 text-xs mt-0.5">{record.expected_delivery_date ? (new Date(record.expected_delivery_date) > new Date() ? p('upcoming') : p('overdue')) : ''}</span></div></td>
                      ) : null}
                      {showCol('total_amount') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{formatBrandMoney(record.total_amount || 0)}</span><span className="text-white/50 text-xs mt-0.5">{p('vat_short')}: {formatBrandMoney(record.tax_amount || 0)}</span></div></td>
                      ) : null}
                      {showCol('payment_method') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{getPaymentMethodLabel(record.payment_method) || '-'}</span><span className="text-white/50 text-xs mt-0.5">{p('fees_short')}: {formatBrandMoney(record.shipping_cost || 0)}</span></div></td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={statusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-1">
                          <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                          {record.status === 'delivered' && (
                            <button
                              type="button"
                              onClick={() => handleConvertToLedger(record)}
                              className="p-2 rounded-lg text-purple-400 hover:text-purple-300 hover:bg-white/10 transition-colors"
                              title={p('convert_to_sale')}
                            >
                              <DollarSign size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><ClipboardList size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_title')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedOrders.length > 0 ? (
              displayedOrders.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><ClipboardList size={20} className="text-emerald-400" />{record.order_number}</h3>
                        <QawaemClickableStatusBadge
                          status={record.status}
                          recordId={record.id}
                          options={statusOptions}
                          onChange={handleStatusChange}
                        />
                        {record.order_date && (<span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">{fmtDate(record.order_date)}</span>)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div><h5 className="font-medium text-white mb-2">{p('card_order')}</h5><div className="space-y-1 text-sm text-white/70"><div><span className="font-medium">{p('label_order_number')}:</span> {record.order_number}</div><div><span className="font-medium">{p('label_date')}:</span> {fmtDate(record.order_date)}</div></div></div>
                        <div><h5 className="font-medium text-white mb-2">{p('label_customer')}</h5><div className="space-y-1 text-sm text-white/70"><div><span className="font-medium">{p('label_name')}:</span> {record.customer_name}</div><div><span className="font-medium">ID:</span> {record.customer_id || 'N/A'}</div></div></div>
                        <div><h5 className="font-medium text-white mb-2">{p('card_amounts')}</h5><div className="space-y-1 text-sm text-white/70"><div><span className="font-medium">{p('label_total')}:</span> <span className="text-white font-bold">{formatBrandMoney(record.total_amount || 0)}</span></div><div><span className="font-medium">{p('label_vat')}:</span> {formatBrandMoney(record.tax_amount || 0)}</div></div></div>
                        <div><h5 className="font-medium text-white mb-2">{p('card_payment')}</h5><div className="space-y-1 text-sm text-white/70"><div><span className="font-medium">{p('label_payment_method')}:</span> {getPaymentMethodLabel(record.payment_method) || '-'}</div>{record.notes && (<div className="mt-2 p-2 bg-white/5 rounded"><p className="text-white/60 text-xs">{record.notes}</p></div>)}</div></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 sm:ms-2">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                      {record.status === 'delivered' && (
                        <button
                          type="button"
                          onClick={() => handleConvertToLedger(record)}
                          className="p-2 rounded-lg text-purple-400 hover:text-purple-300 hover:bg-white/10 transition-colors"
                          title={p('convert_to_sale')}
                        >
                          <DollarSign size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><ClipboardList size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_title')}</p></div>
            )}
          </div>
        )}
      </VentesListPageChrome>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('modal_view_title')}
        icon={Eye}
        maxWidth="1100px"
        testId="commandes-view-modal"
      >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20"><h3 className="text-white font-semibold mb-4 flex items-center gap-2"><ClipboardList size={18} className="text-emerald-400" />{p('section_order_info')}</h3><div className="grid grid-cols-1 md:grid-cols-3 gap-4"><div><p className="text-white/60 text-sm mb-1">{p('label_order_number')}</p><p className="text-white font-medium text-lg">{viewingRecord.order_number}</p></div><div><p className="text-white/60 text-sm mb-1">{p('label_date')}</p><p className="text-white font-medium">{fmtDate(viewingRecord.order_date)}</p></div><div><p className="text-white/60 text-sm mb-1">{p('label_expected_delivery')}</p><p className="text-white font-medium">{fmtDate(viewingRecord.expected_delivery_date)}</p></div><div><p className="text-white/60 text-sm mb-1">{p('status_label')}</p><div className="flex"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status') })} /></div></div></div></div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20"><h3 className="text-white font-semibold mb-4 flex items-center gap-2"><UserCheck size={18} className="text-blue-400" />{p('label_customer')}</h3><div className="grid grid-cols-1 md:grid-cols-3 gap-4"><div><p className="text-white/60 text-sm mb-1">{p('label_name')}</p><p className="text-white font-medium">{viewingRecord.customer_name}</p></div><div><p className="text-white/60 text-sm mb-1">{p('label_id')}</p><p className="text-white font-medium">{viewingRecord.customer_id || 'N/A'}</p></div></div></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20"><h3 className="text-white font-semibold mb-4 flex items-center gap-2"><DollarSign size={18} className="text-green-400" />{p('card_amounts')}</h3><div className="space-y-3"><div><p className="text-white/60 text-sm mb-1">{p('label_subtotal')}</p><p className="text-white font-medium">{formatBrandMoney(viewingRecord.subtotal || 0)}</p></div><div><p className="text-white/60 text-sm mb-1">{p('label_vat')}</p><p className="text-white font-medium">{formatBrandMoney(viewingRecord.tax_amount || 0)}</p></div><div><p className="text-white/60 text-sm mb-1">{p('label_shipping_fees')}</p><p className="text-white font-medium">{formatBrandMoney(viewingRecord.shipping_cost || 0)}</p></div><div className="pt-2 border-t border-white/30"><p className="text-white/60 text-sm mb-1">{p('label_total')}</p><p className="text-white font-bold text-xl">{formatBrandMoney(viewingRecord.total_amount || 0)}</p></div></div></div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20"><h3 className="text-white font-semibold mb-4 flex items-center gap-2"><FileText size={18} className="text-yellow-400" />{p('label_description')}</h3><div className="space-y-3"><div><p className="text-white/60 text-sm mb-1">{p('label_payment_method')}</p><p className="text-white font-medium">{getPaymentMethodLabel(viewingRecord.payment_method) || p('payment_undefined')}</p></div>{viewingRecord.notes && (<div><p className="text-white/60 text-sm mb-1">{p('label_notes')}</p><p className="text-white/80 text-sm bg-white/5 p-3 rounded-lg">{viewingRecord.notes}</p></div>)}</div></div>
                </div>
              </div>
      </QawaemErpViewModal>
      ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingRecord ? p('modal_edit') : p('modal_create')}
        icon={ClipboardList}
        showSaveButton
        formId="order-form"
        saveLabel={editingRecord ? p('update') : p('create')}
        testId="commandes-crud-modal"
        tabs={orderFormTabs}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
      >
        <form id="order-form" onSubmit={handleSubmit}>
          {formTab === 'order' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_order_number')}</label>
                  <input
                    type="text"
                    required
                    value={formData.order_number || ''}
                    onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                    className={VENTES_REF_INPUT_CLASS}
                    dir="ltr"
                  />
                </div>
                <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_order_date_label')} <span className="qawaem-required-mark">*</span></label><input type="date" required value={formData.order_date} onChange={(e) => setFormData({ ...formData, order_date: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_expected_delivery_label')}</label><input type="date" value={formData.expected_delivery_date} onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                <div>
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('status_label')} <span className="qawaem-required-mark">*</span></label>
                  <div className="relative" ref={statusDropdownRef}>
                    <button type="button" data-status-dropdown-button="true" onClick={() => setShowStatusDropdown(!showStatusDropdown)} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between"><span>{statusOptions.find(s => s.value === formData.status)?.label || p('select_placeholder')}</span><ChevronDown size={16} className={`transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} /></button>
                    {showStatusDropdown && ReactDOM.createPortal(<div data-status-dropdown="true" className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', position: 'fixed', ...(statusDropdownRef.current && (() => { const rect = statusDropdownRef.current.getBoundingClientRect(); const spaceBelow = window.innerHeight - rect.bottom; const spaceAbove = rect.top; const dropdownHeight = 260; return spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? { bottom: window.innerHeight - rect.top + 8, left: rect.left, width: rect.width } : { top: rect.bottom + 8, left: rect.left, width: rect.width }; })()), zIndex: 99999, boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>{statusOptions.map(option => (<button key={option.value} type="button" onClick={() => { setFormData({ ...formData, status: option.value }); setShowStatusDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium"><option.icon size={16} className="mr-2" />{option.label}</button>))}</div>, document.body)}
                  </div>
                </div>
                <div className="md:col-span-3">
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_customer')} <span className="qawaem-required-mark">*</span></label>
                  <CustomSelect value={formData.customer_id} onChange={(value) => handleClientSelection(value)} options={availableClients.map(client => ({ value: client.id.toString(), label: `${client.company_name || client.name || p('label_customer')}${client.contact_person ? ` - ${client.contact_person}` : ''}${client.phone1 ? ` - ${client.phone1}` : ''}` }))} placeholder={p('placeholder_select_client')} disabled={loadingClients} searchable />
                  {loadingClients && (<p className="text-white/50 text-xs mt-1">{p('loading_clients')}</p>)}
                </div>
                <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_shipping_cost')}</label><input type="number" step="0.01" value={formData.shipping_cost} onChange={(e) => setFormData({ ...formData, shipping_cost: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_decimal')} /></div>
                <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_payment_method')}</label><CustomSelect value={formData.payment_method} onChange={(value) => setFormData({ ...formData, payment_method: value })} options={paymentMethodOptions} placeholder={p('payment_method_placeholder')} /></div>
                <div className="md:col-span-3"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" rows={3} placeholder={p('placeholder_notes')} /></div>
              </div>
            </div>
          )}
          {formTab === 'products' && (
            <div className="space-y-4">
              <button type="button" onClick={addOrderItem} className="w-full bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 text-blue-200 px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 font-medium"><Plus size={18} />{p('add_product_btn')}</button>
              {orderItems.length > 0 && (
                <div className="glass-table-scroll overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b border-white/10"><th className="px-2 py-2 text-left text-white/70 text-xs font-medium">#</th><th className="px-2 py-2 text-left text-white/70 text-xs font-medium">{p('field_product_required')}</th><th className="px-2 py-2 text-left text-white/70 text-xs font-medium">{p('col_qty')}</th><th className="px-2 py-2 text-left text-white/70 text-xs font-medium">{p('col_unit_price_short')}</th><th className="px-2 py-2 text-left text-white/70 text-xs font-medium">{p('col_line_total')}</th><th className="px-2 py-2 text-center text-white/70 text-xs font-medium">{p('col_action')}</th></tr></thead>
                    <tbody>
                      {orderItems.map((item, index) => (
                        <tr key={item.id} className="border-b border-white/5">
                          <td className="px-2 py-2 text-white text-sm">{index + 1}</td>
                          <td className="px-2 py-2"><select value={item.product_id} onChange={(e) => updateOrderItem(item.id, 'product_id', e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required><option value="">{p('select_placeholder')}</option>{availableProducts.map(product => (<option key={product.id} value={product.id}>{product.name} - {formatBrandMoney(product.price || 0)}</option>))}</select></td>
                          <td className="px-2 py-2"><input type="number" min="1" value={item.quantity} onChange={(e) => updateOrderItem(item.id, 'quantity', e.target.value)} className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required /></td>
                          <td className="px-2 py-2 text-white text-sm">{formatBrandMoney(item.unit_price || 0)}</td>
                          <td className="px-2 py-2 text-white font-semibold text-sm">{formatBrandMoney(item.line_total || 0)}</td>
                          <td className="px-2 py-2 text-center"><button type="button" onClick={() => removeOrderItem(item.id)} className="p-1.5 text-red-400 hover:text-red-300 transition-colors" title={p('action_delete')}><Trash2 size={16} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="bg-white/5 rounded-xl p-4 space-y-2 border border-white/10">
                <div className="flex justify-between items-center"><span className="text-white/70 text-sm">{p('subtotal_label')}</span><span className="text-white font-semibold">{formatBrandMoney(parseFloat(formData.subtotal) || 0)}</span></div>
                <div className="flex justify-between items-center"><span className="text-white/70 text-sm">{p('vat_rate_label', { rate: vatRate })}</span><span className="text-white font-semibold">{formatBrandMoney(parseFloat(formData.tax_amount) || 0)}</span></div>
                <div className="flex justify-between items-center pt-2 border-t border-white/20"><span className="text-white font-bold">{p('label_total')}:</span><span className="text-white font-bold text-xl">{formatBrandMoney(parseFloat(formData.total_amount) || 0)}</span></div>
              </div>
            </div>
          )}
        </form>
      </QawaemErpCrudModal>
    </>
  );
};

export default CommandesClientsPage;


